import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Compass, Sparkles, TrendingUp, ArrowUpRight, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { SpecialDrawsPanel } from "@/components/SpecialDrawsPanel";

const SORTS = [
  { key: "best_odds", label: "Best draw odds" },
  { key: "lowest_points", label: "Lowest points" },
  { key: "least_creep", label: "Least point creep" },
  { key: "most_public", label: "Most public land" },
  { key: "premium", label: "Premium / hardest first" },
];

const TIER = {
  strong: { label: "Draw this year", cls: "bg-[#2E7D46]/12 text-[#2E7D46] border-[#2E7D46]/30" },
  possible: { label: "Realistic", cls: "bg-[#B7761F]/12 text-[#B7761F] border-[#B7761F]/30" },
  longshot: { label: "Long shot", cls: "bg-[#5C6B61]/10 text-[#5C6B61] border-[#5C6B61]/25" },
  unknown: { label: "—", cls: "bg-[#5C6B61]/10 text-[#5C6B61] border-[#5C6B61]/25" },
};

// Render the AI analysis (markdown-lite: **Header** lines + reasons).
function Analysis({ text }) {
  const lines = (text || "").split("\n").filter((l) => l.trim());
  return (
    <div className="space-y-2.5" data-testid="analyze-output">
      {lines.map((l, i) => {
        const m = l.match(/^\s*\*\*(.+?)\*\*[:：]?\s*(.*)$/);
        if (m) {
          return (
            <div key={i}>
              <p className="font-semibold text-forest text-sm">{m[1]}</p>
              {m[2] && <p className="text-charcoal/70 text-sm leading-snug">{m[2].replace(/\*\*/g, "")}</p>}
            </div>
          );
        }
        return <p key={i} className="text-charcoal/70 text-sm leading-snug">{l.replace(/\*\*/g, "")}</p>;
      })}
    </div>
  );
}

export function PlannerExplorer() {
  const { openAI } = useUI();
  const [params, setParams] = useSearchParams();
  const [states, setStates] = useState([]);
  const [speciesOpts, setSpeciesOpts] = useState([]);
  const [system, setSystem] = useState(null);
  const [f, setF] = useState({ state: "", species: "", points: "" });
  const [residency, setResidency] = useState("all");
  const [weapon, setWeapon] = useState("all");
  const [opportunity, setOpportunity] = useState("all");
  const [sort, setSort] = useState("best_odds");
  const [hunts, setHunts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => { api.exploreStates().then(setStates).catch(() => {}); }, []);

  const covered = useMemo(
    () => [...states].sort((a, b) => (b.has_data - a.has_data) || a.state.localeCompare(b.state)),
    [states]);

  // Load species + point system when a state is chosen.
  useEffect(() => {
    setSpeciesOpts([]); setSystem(null);
    if (!f.state) return;
    api.exploreSpecies(f.state).then((d) => { setSpeciesOpts(d.species || []); setSystem(d.system || null); }).catch(() => {});
  }, [f.state]);

  const runSearch = useCallback(async (state, species, points) => {
    if (!state || !species) return;
    setLoading(true); setAnalysis(null);
    try {
      const p = {};
      if (species) p.species = species;
      if (points !== "" && points != null) p.points = Number(points);
      const d = await api.exploreHunts(state, p);
      setHunts(d.hunts || []);
    } catch { toast.error("Couldn't load hunts."); setHunts([]); }
    setLoading(false);
  }, []);

  // Deep-link support (AI -> Planner): ?state=&species=&points= (&special=1 scrolls to special draws)
  useEffect(() => {
    const st = params.get("state"); const sp = params.get("species"); const pt = params.get("points");
    if (st) {
      setF({ state: st, species: sp || "", points: pt || "" });
      if (sp) { runSearch(st, sp, pt || ""); document.getElementById("planner-explorer")?.scrollIntoView({ behavior: "smooth" }); }
      if (params.get("special")) setTimeout(() => document.getElementById("special-draws")?.scrollIntoView({ behavior: "smooth" }), 400);
      setParams({}, { replace: true });
    }
  }, []); // run once on mount for deep-link

  const search = () => {
    if (!f.state || !f.species) { toast.error("Pick a state and species."); return; }
    setResidency("all"); setWeapon("all"); setOpportunity("all");
    runSearch(f.state, f.species, f.points);
  };

  const weaponOpts = useMemo(() => [...new Set((hunts || []).map((h) => h.weapon).filter(Boolean))], [hunts]);
  const oppOpts = useMemo(() => [...new Set((hunts || []).map((h) => h.opportunity_type).filter(Boolean))], [hunts]);
  const residencyOpts = useMemo(() => [...new Set((hunts || []).map((h) => h.residency).filter(Boolean))], [hunts]);

  const filtered = useMemo(() => {
    let rows = [...(hunts || [])];
    if (residency !== "all") rows = rows.filter((h) => (h.residency || "").toLowerCase() === residency);
    if (weapon !== "all") rows = rows.filter((h) => h.weapon === weapon);
    if (opportunity !== "all") rows = rows.filter((h) => h.opportunity_type === opportunity);
    const odds = (h) => (h.personalized?.est_odds_pct ?? h.draw_odds_pct ?? 0);
    const pts = (h) => (h.min_points ?? 999);
    const creep = (h) => (h.creep?.applicable ? (h.creep.avg_annual_creep ?? 99) : 99);
    const pub = (h) => (h.public_land_pct ?? -1);
    const sorters = {
      best_odds: (a, b) => odds(b) - odds(a),
      lowest_points: (a, b) => pts(a) - pts(b),
      least_creep: (a, b) => creep(a) - creep(b),
      most_public: (a, b) => pub(b) - pub(a),
      premium: (a, b) => pts(b) - pts(a),
    };
    return rows.sort(sorters[sort] || sorters.best_odds);
  }, [hunts, residency, weapon, opportunity, sort]);

  const analyze = async () => {
    setAnalyzing(true); setAnalysis(null);
    try {
      const d = await api.plannerAnalyze({
        state: f.state, species: f.species,
        residency: residency !== "all" ? residency : undefined,
        weapon: weapon !== "all" ? weapon : undefined,
        opportunity_type: opportunity !== "all" ? opportunity : undefined,
        points: f.points !== "" ? Number(f.points) : undefined,
      });
      setAnalysis(d.analysis);
    } catch { toast.error("Analysis failed. Try again."); }
    setAnalyzing(false);
  };

  const continueInAI = () => {
    const pts = f.points !== "" ? ` with ${f.points} points` : "";
    openAI(`Give me a draw strategy for ${f.species} in ${f.state}${pts}. Best units to apply for this year and long term?`, true);
  };

  const inp = "border border-saddle/25 bg-white px-3 py-2.5 text-sm outline-none focus:border-forest w-full";
  const hasPoints = system?.has_points !== false;

  return (
    <section id="planner-explorer" data-testid="planner-explorer">
      <div className="flex items-center gap-2 text-forest">
        <Compass size={18} className="text-blaze" />
        <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">Explore draw hunts</h2>
      </div>
      <p className="text-sm text-charcoal/55 mt-1">Pick a state &amp; species, add your points, and see every verified hunt you could apply for — ranked to you.</p>

      {/* Guided filters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 bg-white border border-saddle/20 p-4">
        <select value={f.state} onChange={(e) => setF({ state: e.target.value, species: "", points: "" })} className={inp} data-testid="explorer-state">
          <option value="">Select state…</option>
          {covered.map((s) => <option key={s.state} value={s.state}>{s.state}{s.has_data ? "" : " (coverage in progress)"}</option>)}
        </select>
        <select value={f.species} onChange={(e) => setF({ ...f, species: e.target.value })} className={inp} data-testid="explorer-species" disabled={!speciesOpts.length}>
          <option value="">{f.state && !speciesOpts.length ? "No verified species yet" : "Select species…"}</option>
          {speciesOpts.map((s) => <option key={s.species} value={s.species}>{s.species}</option>)}
        </select>
        <input type="number" min="0" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })}
          placeholder={hasPoints ? "Your points" : "No points in this state"} disabled={!hasPoints}
          className={`${inp} disabled:bg-paper disabled:text-charcoal/40`} data-testid="explorer-points" />
        <button onClick={search} disabled={loading} className="bg-forest text-bone font-semibold uppercase tracking-wider text-sm px-4 py-2.5 inline-flex items-center justify-center gap-2 hover:bg-blaze transition-colors disabled:opacity-60" data-testid="explorer-search">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Find hunts
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="py-14 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : hunts === null ? null : hunts.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-saddle/30 mt-5" data-testid="explorer-empty">
          <p className="font-serif text-xl text-forest">No verified hunts for this pick yet</p>
          <p className="text-charcoal/60 mt-2 text-sm">HomeRange only shows hunts confirmed against official state sources. Coverage is in progress.</p>
        </div>
      ) : (
        <div className="mt-5" data-testid="explorer-results">
          {/* Refine + sort + analyze */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {residencyOpts.length > 1 && (
              <select value={residency} onChange={(e) => setResidency(e.target.value)} className="border border-saddle/25 bg-white px-2.5 py-1.5 text-xs" data-testid="explorer-filter-residency">
                <option value="all">All residency</option>
                {residencyOpts.map((r) => <option key={r} value={r.toLowerCase()}>{r}</option>)}
              </select>
            )}
            {weaponOpts.length > 1 && (
              <select value={weapon} onChange={(e) => setWeapon(e.target.value)} className="border border-saddle/25 bg-white px-2.5 py-1.5 text-xs" data-testid="explorer-filter-weapon">
                <option value="all">All weapons</option>
                {weaponOpts.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            )}
            {oppOpts.length > 1 && (
              <select value={opportunity} onChange={(e) => setOpportunity(e.target.value)} className="border border-saddle/25 bg-white px-2.5 py-1.5 text-xs" data-testid="explorer-filter-opportunity">
                <option value="all">All opportunities</option>
                {oppOpts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
              </select>
            )}
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-saddle/25 bg-white px-2.5 py-1.5 text-xs ml-auto" data-testid="explorer-sort">
              {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
            </select>
            <button onClick={analyze} disabled={analyzing} className="bg-blaze text-white text-xs font-semibold uppercase tracking-wide px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-blaze-dark transition-colors disabled:opacity-60" data-testid="explorer-analyze">
              {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Analyze my options
            </button>
          </div>

          {/* AI analysis panel */}
          <AnimatePresence>
            {(analyzing || analysis) && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="bg-forest/[0.04] border border-forest/20 rounded-lg p-4 mb-4 overflow-hidden" data-testid="explorer-analysis">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={15} className="text-blaze" />
                  <span className="hr-overline text-[11px] text-forest">HomeRange AI · Your best options</span>
                </div>
                {analyzing ? (
                  <div className="flex items-center gap-2 text-charcoal/60 text-sm py-3"><Loader2 size={15} className="animate-spin" /> Analyzing your verified options…</div>
                ) : (
                  <>
                    <Analysis text={analysis} />
                    <button onClick={continueInAI} className="mt-3 text-blaze text-xs font-semibold inline-flex items-center gap-1 hover:underline" data-testid="explorer-continue-ai">
                      Continue in HomeRange AI <ArrowUpRight size={13} />
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results table */}
          <div className="overflow-x-auto border border-saddle/20 bg-white">
            <table className="w-full text-sm min-w-[720px]" data-testid="explorer-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-charcoal/45 border-b border-saddle/15">
                  <th className="px-4 py-2.5 font-semibold">Hunt / Unit</th>
                  <th className="px-3 py-2.5 font-semibold">Your odds</th>
                  <th className="px-3 py-2.5 font-semibold">Points</th>
                  <th className="px-3 py-2.5 font-semibold">Point creep</th>
                  <th className="px-3 py-2.5 font-semibold">Public land</th>
                  <th className="px-3 py-2.5 font-semibold">Outlook</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-saddle/10">
                {filtered.map((h) => {
                  const tier = TIER[h.tier?.key] || (h.personalized?.eligibility === "meets" ? TIER.strong : h.personalized?.est_odds_pct >= 25 ? TIER.possible : TIER.longshot);
                  const est = h.personalized?.est_odds_pct;
                  return (
                    <tr key={h.id} className="hover:bg-paper/60 transition-colors" data-testid={`explorer-row-${h.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-serif text-base text-forest leading-tight">{h.unit}</p>
                        <p className="text-[11px] text-charcoal/55">{h.weapon} · {h.residency}{h.season ? ` · ${h.season}` : ""}</p>
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-serif text-lg text-blaze tabular-nums">{est != null ? `${est}%` : `${h.draw_odds_pct ?? "—"}%`}</span>
                        <span className="block text-[10px] text-charcoal/40">{est != null ? "your est." : "state"}</span>
                      </td>
                      <td className="px-3 py-3 tabular-nums text-charcoal/75">
                        {h.point_system_type === "random" || h.point_system_type === "none" ? <span className="text-charcoal/45">n/a</span>
                          : h.point_system_type && h.point_system_type !== "preference" ? <span title="Bonus system — no guaranteed threshold" className="text-charcoal/60">bonus</span>
                          : (h.min_points != null ? `~${h.min_points}` : "—")}
                      </td>
                      <td className="px-3 py-3 text-[12px] text-charcoal/70">
                        {h.creep?.applicable ? (
                          <span className="inline-flex items-center gap-1" title={h.creep.projection?.label || ""}>
                            <TrendingUp size={11} className="text-saddle" />
                            {h.creep.avg_annual_creep != null ? `+${h.creep.avg_annual_creep}/yr` : "—"}
                            {h.creep.projection && <span className="text-charcoal/40"> · ~{h.creep.projection.next_year_low}{h.creep.projection.next_year_high !== h.creep.projection.next_year_low ? `–${h.creep.projection.next_year_high}` : ""} pts*</span>}
                          </span>
                        ) : <span className="text-charcoal/40">n/a</span>}
                      </td>
                      <td className="px-3 py-3 text-charcoal/70">{h.public_land_pct != null ? `${h.public_land_pct}%` : <span className="text-charcoal/40">not available</span>}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 border ${tier.cls}`}>{tier.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-charcoal/45 mt-2 flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-[#2E7D46]" /> Verified state data · personalized odds are transparent estimates. *Point figures marked with an asterisk are HomeRange Projections, not official results.
          </p>
        </div>
      )}

      {/* §11–12: special draws & raffles — filtered to the explored state/species when picked */}
      <div className="mt-10">
        <SpecialDrawsPanel state={f.state || undefined} species={f.species || undefined} />
      </div>
    </section>
  );
}
